<script setup lang="ts">
import { ref } from 'vue';
import type { AdminRefusal } from '../order-management';
const props = defineProps<{
  orderId: string;
  canRefuse?: boolean;
  refusal?: AdminRefusal;
  disabled?: boolean;
}>();
const emit = defineEmits<{ changed: [] }>();
const reason = ref(''),
  notDelivered = ref(false),
  notConsumed = ref(false),
  busy = ref(false),
  error = ref('');
async function submit() {
  if (busy.value || props.disabled) return;
  if (!reason.value.trim() || !notDelivered.value || !notConsumed.value) {
    error.value = 'Indiquez le motif et confirmez les deux contrôles.';
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/delivery-requests/${encodeURIComponent(props.orderId)}/refuse-unpaid`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          reason: reason.value.trim(),
          notDelivered: true,
          notConsumed: true,
        }),
      },
    );
    if (!response.ok)
      throw new Error(
        'Refus enregistré ou non confirmé : actualisez pour vérifier le traitement bancaire. Aucun remboursement ni paiement forcé.',
      );
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
    emit('changed');
  }
}
</script>
<template>
  <section v-if="canRefuse || refusal" class="refusal" aria-label="Refus administrateur">
    <h3>Refus de commande</h3>
    <template v-if="refusal?.status === 'REFUSED'">
      <p role="status">Commande refusée · empreinte libérée, aucun prélèvement.</p>
      <p>{{ refusal.reason }}</p>
      <p>Retirée de la liste préparateur. L’historique est conservé.</p>
    </template>
    <template v-else>
      <p v-if="refusal" role="alert">
        Commande suspendue et retirée de la liste préparateur. La libération bancaire reste à
        confirmer.
      </p>
      <p>
        Pour une commande non prélevée uniquement. L’empreinte sera libérée après confirmation
        bancaire. Un paiement déjà prélevé nécessite le processus de remboursement.
      </p>
      <fieldset :disabled="busy || disabled">
        <label><input v-model="notDelivered" type="checkbox" /> Non-livraison vérifiée</label>
        <label><input v-model="notConsumed" type="checkbox" /> Non-consommation vérifiée</label>
        <label>Motif du refus<textarea v-model="reason" maxlength="128" /></label>
        <button
          type="button"
          :disabled="!notDelivered || !notConsumed || !reason.trim()"
          @click="submit"
        >
          {{
            busy
              ? 'Vérification bancaire…'
              : refusal
                ? 'Vérifier la libération de l’empreinte'
                : 'Refuser la commande et libérer l’empreinte'
          }}
        </button>
      </fieldset>
    </template>
    <p v-if="error" role="alert">{{ error }}</p>
  </section>
</template>
<style scoped>
.refusal {
  border: 1px solid #b91c1c;
  border-radius: 0.8rem;
  padding: 1rem;
  margin: 1rem 0;
  overflow-wrap: anywhere;
}
fieldset {
  border: 0;
  padding: 0;
  min-width: 0;
}
label {
  display: block;
  margin: 0.7rem 0;
}
textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 5rem;
}
button {
  width: 100%;
  min-height: 48px;
  padding: 0.7rem;
  background: #b91c1c;
  color: white;
  border: 0;
  border-radius: 0.5rem;
  font-weight: 700;
}
button:disabled {
  opacity: 0.55;
}
</style>
